import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import RansomwareIncidentDetail from './ransomware-incident-detail.vue'
import RansomwareIncidentList from './_components/ransomware-incident-list/ransomware-incident-list.vue'
import { reactive, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StatisticsCards from './_components/statistics-cards.vue'
import InfectionPhaseEvents from './_components/infection-phase-events.vue'
import ExecutionPhaseEvents from './_components/execution-phase-events.vue'
import DefenseConfig from './_components/defense-config.vue'
import {
  PermissionFe,
  ServicePermissionService,
} from '@/services/service-permission.service'

import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  EventListItem,
  RansomwareService,
  RequestParams,
  SecurityEventResponse,
} from './service'
import { PolicySettingType, PolicyType } from '../../_enum/policy-type'
import { PolicyCommonService } from '../../policy/policy-common.service'
import { ElMessage } from 'element-plus'
import Card from '../../_components/card/card.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { ProtectionType, trProtectionType } from './types/ransomware-incident'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import type { RansomwareIncidentStatCardKey } from './_components/ransomware-incident-statistics-cards.types'


export default /*#__PURE__*/_defineComponent({
  __name: 'ransomware-incident',
  props: {
  flag: {
    type: Boolean,
    default: true,
  },
  loadMoreMode: {
    type: Boolean,
    default: false,
  },
  deviceId: {
    type: String,
  },
},
  setup(__props) {

const { t, locale } = useI18n()

/** 将接口返回的 statistics 与阶段固定类型合并，未返回的类型 count 为 0 */
function mapPhaseStats(
  statistics: Array<{ defence_type?: number | string; count?: number }> | undefined,
  phaseTypes: ProtectionType[]
) {
  const countByType = new Map<number, number>()
  for (const item of statistics || []) {
    const defenceType = Number(item.defence_type)
    if (phaseTypes.includes(defenceType as ProtectionType)) {
      countByType.set(defenceType, Number(item.count) || 0)
    }
  }
  return phaseTypes
    .map((defenceType) => ({
      name: trProtectionType(t, defenceType) || t('ransomwareIncident.unknown'),
      count: countByType.get(defenceType) ?? 0,
      type: defenceType as ProtectionType
    }))
    .sort((a, b) => b.count - a.count)
}

const props = __props
const injector = useReflectiveInjector([RansomwareService, PolicyCommonService])
const ransomwareService = injector.get(RansomwareService)
const groupPolicySettingsService = injector.get(PolicyCommonService)
const saveOrUpdateApi = groupPolicySettingsService.saveSettings.bind(
  groupPolicySettingsService,
)
const getByType = groupPolicySettingsService.getByType.bind(
  groupPolicySettingsService,
)
const servicePermissionService = injector.get(ServicePermissionService)
const hasPermission = servicePermissionService.getHasPermission(
  PermissionFe.ransomware_protect,
)
const detailInfo = ref({} as EventListItem)
const visibleDetail = ref(false)
const ransomwareIncidentListRef = ref()
const incidentDrawerVisible = ref(false)
const incidentDrawerListKey = ref(0)
/** 侧栏「已阻止事件」：已阻止(0)+阻止并结束(2)，请求带 statuses: [0, 2] */
const incidentDrawerInitialStatusList = ref<number[] | undefined>(undefined)
/** 统计卡 / 阶段排行点击侧栏：抽屉内时间不设默认，与「查看全部」区分 */
const incidentDrawerInitialDateEmpty = ref(false)
/** 阶段排行点击：按防护类型预筛；统计卡 / 查看全部为 undefined */
const incidentDrawerInitialDefenceType = ref<number | undefined>(undefined)
/** 阶段排行点击且非「全部」时：与 getTimeRangeByEnum 一致的 Unix 秒区间 */
const incidentDrawerInitialTimeRangeUnix = ref<[number, number] | undefined>(undefined)

// 统计数据
const statistics = reactive({
  ransomwareEventCount: 0,
  blockedEventCount: 0
})

// 投毒/入侵阶段防护事件（类型：5-暴力破解, 6-漏洞利用, 7-勒索特征识别）
const infectionTimeRange = ref<TimeRange>(TimeRange.ALL)
const infectionPhaseData = ref<Array<{ name: string; count: number; type: ProtectionType }>>([])

// 执行/破坏阶段防护事件（类型：1-诱饵文件篡改, 2-其他文件篡改, 3-主导记录(MBR)篡改, 4-卷影删除）
const executionTimeRange = ref<TimeRange>(TimeRange.ALL)
const executionPhaseData = ref<Array<{ name: string; count: number; type: ProtectionType }>>([])

// 加载统计数据
async function loadStatistics() {
  if (!hasPermission) return

  try {
    // 调用API获取统计数据
    const params: Partial<RequestParams> = {
      group_id: -1,
      defence_type: -1,
      page: 1,
      limit: 1,
      device_id: '',
    }

    const res = await ransomwareService.getRansomwareProtectList(params as RequestParams)

    // 更新统计卡片数据
    statistics.ransomwareEventCount = res.total || 0
    statistics.blockedEventCount = res.blocked_count || 0
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载投毒/入侵阶段防护事件数据
async function loadInfectionPhaseData() {
  if (!hasPermission) return

  try {
    const [start_time, end_time] = getTimeRangeByEnum(infectionTimeRange.value)
    const params: any = {}
    if (start_time !== undefined && end_time !== undefined) {
      params.start_time = start_time.toString()
      params.end_time = end_time.toString()
    }
    const res = await ransomwareService.getRansomwareprotectStats(params)
    const infectionTypes = [
      ProtectionType.BruteForceCracking,
      ProtectionType.Exploit,
      ProtectionType.RansomwareSignatureIdentification
    ]
    infectionPhaseData.value = mapPhaseStats(res.statistics, infectionTypes)
  } catch (error) {
    console.error('加载投毒/入侵阶段防护事件数据失败:', error)
    infectionPhaseData.value = mapPhaseStats(undefined, [
      ProtectionType.BruteForceCracking,
      ProtectionType.Exploit,
      ProtectionType.RansomwareSignatureIdentification
    ])
  }
}

// 加载执行/破坏阶段防护事件数据
async function loadExecutionPhaseData() {
  if (!hasPermission) return

  try {
    const [start_time, end_time] = getTimeRangeByEnum(executionTimeRange.value)
    const params: any = {}
    if (start_time !== undefined && end_time !== undefined) {
      params.start_time = start_time.toString()
      params.end_time = end_time.toString()
    }
    const res = await ransomwareService.getRansomwareprotectStats(params)
    const executionTypes = [
      ProtectionType.DecoyFileTampering,
      ProtectionType.OtherFileTampering,
      ProtectionType.DominantRecord,
      ProtectionType.Tamper
    ]
    executionPhaseData.value = mapPhaseStats(res.statistics, executionTypes)
  } catch (error) {
    console.error('加载执行/破坏阶段防护事件数据失败:', error)
    executionPhaseData.value = mapPhaseStats(undefined, [
      ProtectionType.DecoyFileTampering,
      ProtectionType.OtherFileTampering,
      ProtectionType.DominantRecord,
      ProtectionType.Tamper
    ])
  }
}

// 监听列表数据变化，更新统计数据
function handleListDataUpdate(response: SecurityEventResponse) {
  if (response) {
    statistics.ransomwareEventCount = response.total || 0
    statistics.blockedEventCount = response.blocked_count || 0
  }
}

function viewAllIncidents() {
  if (!hasPermission) return
  incidentDrawerInitialStatusList.value = undefined
  incidentDrawerInitialDefenceType.value = undefined
  incidentDrawerInitialTimeRangeUnix.value = undefined
  incidentDrawerInitialDateEmpty.value = false
  incidentDrawerListKey.value += 1
  incidentDrawerVisible.value = true
}

/** 顶栏统计卡点击：全部事件 / 已阻止+阻止并结束（statuses: [0,2]，见 service） */
function handleRansomwareIncidentStatClick(key: RansomwareIncidentStatCardKey) {
  if (key === 'allIncidents') {
    if (statistics.ransomwareEventCount <= 0) return
    incidentDrawerInitialStatusList.value = undefined
  } else {
    if (statistics.blockedEventCount <= 0) return
    incidentDrawerInitialStatusList.value = [0, 2]
  }
  incidentDrawerInitialDefenceType.value = undefined
  incidentDrawerInitialTimeRangeUnix.value = undefined
  incidentDrawerInitialDateEmpty.value = true
  incidentDrawerListKey.value += 1
  incidentDrawerVisible.value = true
}

/** 投毒/入侵、执行/破坏阶段排行点击：「全部」则侧栏时间不限制；否则与排行图一致的时间区间 + defence_type 筛 */
function handlePhaseRankingRowClick(payload: {
  type: ProtectionType
  count: number
  chartTimeRange: TimeRange
}) {
  if (payload.count <= 0) return
  incidentDrawerInitialStatusList.value = undefined
  incidentDrawerInitialDefenceType.value = payload.type
  const tr = payload.chartTimeRange
  if (tr === TimeRange.ALL) {
    incidentDrawerInitialDateEmpty.value = true
    incidentDrawerInitialTimeRangeUnix.value = undefined
  } else {
    incidentDrawerInitialDateEmpty.value = false
    const [startSec, endSec] = getTimeRangeByEnum(tr)
    if (startSec !== undefined && endSec !== undefined) {
      incidentDrawerInitialTimeRangeUnix.value = [startSec, endSec]
    } else {
      incidentDrawerInitialDateEmpty.value = true
      incidentDrawerInitialTimeRangeUnix.value = undefined
    }
  }
  incidentDrawerListKey.value += 1
  incidentDrawerVisible.value = true
}

function handleCardExport(type: 0 | 1) {
  ransomwareIncidentListRef.value?.download(type)
}

function handleDetail(item: EventListItem) {
  detailInfo.value = item
  visibleDetail.value = true
}
const getType = async () => {
  const result = await getByType({
      type: ~~PolicySettingType.ransomWareProtect,
    }).catch()
    if (!result.data) return
    let data = result.data
    let strategy = JSON.parse(data?.strategy || '{ransomwareprotect: {whitelist:[]}}}')
  return { data, strategy }
}
const addWhiteList = async (val) => {
  const { data, strategy }: any = await getType().catch()
  if (strategy.ransomwareprotect.whitelist.includes(val)) {
      ElMessage({
        type: 'error',
        message: t('ransomwareIncident.whitelistDuplicate'),
      })
      return
    }
    strategy.ransomwareprotect.whitelist.push(val)
    const formData = {
      strategy_type: PolicyType.ransomWareProtect,
      type: ~~PolicySettingType.ransomWareProtect,
      id: data?.id,
      name: t('ransomwareIncident.ransomwareProtection'),
      group_ids: [-1],
      bind_mode: 0,
      device_ids: [],
      strategy: JSON.stringify(strategy),
    }
    saveOrUpdateApi(formData).then((res) => {
      const ok = res?.resp_common?.msg === 'ok'
      ElMessage({
        type: ok ? 'success' : 'error',
        message: ok ? t('ransomwareIncident.opSuccess') : t('ransomwareIncident.opFailed'),
      })
    })
}
const handleItem = (state) => {
  if (!hasPermission) return
  ransomwareIncidentListRef.value?.tableRef?.getList()
}

// 监听时间范围变化
watch(infectionTimeRange, () => {
  loadInfectionPhaseData()
})

watch(executionTimeRange, () => {
  loadExecutionPhaseData()
})

watch(locale, () => {
  if (!hasPermission) return
  loadInfectionPhaseData()
  loadExecutionPhaseData()
})

// 初始化加载统计数据
onMounted(() => {
  loadStatistics()
  loadInfectionPhaseData()
  loadExecutionPhaseData()
})

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.flag)
      ? (_openBlock(), _createBlock(PageSubscribeBar, {
          key: 0,
          "normal-permission-key": _unref(PermissionFe).ransomware_protect
        }, null, 8, ["normal-permission-key"]))
      : _createCommentVNode("", true),
    _createVNode(StatisticsCards, {
      "ransomware-event-count": statistics.ransomwareEventCount,
      "blocked-event-count": statistics.blockedEventCount,
      onStatClick: handleRansomwareIncidentStatClick
    }, null, 8, ["ransomware-event-count", "blocked-event-count"]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "events-section",
      style: {"height":"auto","max-height":"380px"}
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(InfectionPhaseEvents, {
              "time-range": infectionTimeRange.value,
              "onUpdate:timeRange": _cache[0] || (_cache[0] = ($event: any) => ((infectionTimeRange).value = $event)),
              data: infectionPhaseData.value,
              onRowClick: handlePhaseRankingRowClick
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(ExecutionPhaseEvents, {
              "time-range": executionTimeRange.value,
              "onUpdate:timeRange": _cache[1] || (_cache[1] = ($event: any) => ((executionTimeRange).value = $event)),
              data: executionPhaseData.value,
              onRowClick: handlePhaseRankingRowClick
            }, null, 8, ["time-range", "data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      title: _ctx.$t('ransomwareIncident.eventTitle'),
      size: "second",
      class: "cm-ui-mt16",
      onViewMore: viewAllIncidents,
      "view-more-disabled": !_unref(hasPermission),
      viewMoreText: _ctx.$t('ransomwareIncident.viewAll'),
      viewMoreButtonType: ""
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      headerRight: _withCtx(() => [
        _createVNode(ExportDropdown, {
          "csv-i18n-key": "ransomwareIncident.exportCsv",
          "excel-i18n-key": "ransomwareIncident.exportExcel",
          "has-permission": _unref(hasPermission),
          disabled: !statistics.ransomwareEventCount,
          onExport: handleCardExport
        }, null, 8, ["has-permission", "disabled"])
      ]),
      default: _withCtx(() => [
        _createVNode(RansomwareIncidentList, {
          ref_key: "ransomwareIncidentListRef",
          ref: ransomwareIncidentListRef,
          flag: __props.flag,
          "has-permission": _unref(hasPermission),
          onDataUpdate: handleListDataUpdate,
          onDetail: handleDetail
        }, null, 8, ["flag", "has-permission"])
      ]),
      _: 1
    }, 8, ["title", "view-more-disabled", "viewMoreText"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('ransomwareIncident.allIncidents'),
      modelValue: incidentDrawerVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((incidentDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(RansomwareIncidentList, {
          key: incidentDrawerListKey.value,
          viewMode: _unref(TableViewMode).full,
          flag: __props.flag,
          "has-permission": _unref(hasPermission),
          "initial-status-list": incidentDrawerInitialStatusList.value ?? [],
          "initial-date-empty": incidentDrawerInitialDateEmpty.value,
          "initial-time-range-unix": incidentDrawerInitialTimeRangeUnix.value,
          "initial-defence-type": incidentDrawerInitialDefenceType.value,
          onDetail: handleDetail,
          style: {"padding":"10px 16px"}
        }, null, 8, ["viewMode", "flag", "has-permission", "initial-status-list", "initial-date-empty", "initial-time-range-unix", "initial-defence-type"]))
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(DefenseConfig, { hasPermission: _unref(hasPermission) }, null, 8, ["hasPermission"]),
    _createVNode(RansomwareIncidentDetail, {
      modelValue: visibleDetail.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((visibleDetail).value = $event)),
      onAddWhiteList: addWhiteList,
      detailInfo: detailInfo.value
    }, null, 8, ["modelValue", "detailInfo"])
  ], 64))
}
}

})